import type { Sesjonskrav, Sesjonsspoerring } from '../auth/typer.ts';
import type { AdminAdminSession } from '../generated/api/types.gen.ts';
import { sessionGet } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { lagApiKlient } from './klient.ts';

/**
 * `GET /v1/admin/session` — luken innloggingen spør FØR låsen (K8, 2026-09-21).
 *
 * Hvorfor en fabrikk og ikke en React Query-hook som resten av `src/api/**`: kallet skjer
 * i `AuthProvider`, som ligger OVER `ApiKlientProvider` i treet — panelets klient bygges
 * inne i den tilstanden luken skal avgjøre. En hook her ville krevd en andre QueryClient
 * eller at klienten flyttet opp over innloggingen, og begge gjør autentiseringsstien
 * bredere enn den er i dag.
 *
 * Formen er derfor en port (`Sesjonsspoerring` i `src/auth/typer.ts`) med adapteren her:
 * den genererte klienten blir liggende i `src/api/**` som vakt 2 krever, `AuthProvider`
 * kjenner bare en funksjonstype, og `App.tsx` er det ene stedet de to møtes — nøyaktig
 * samme oppdeling som `AdminAuth` og `supabase.ts` har.
 *
 * Klienten bygges per kall. Det skjer noen få ganger per innlogging, tokenet er dermed
 * alltid det ferske, og alternativet — en klient med en mutabel token-variabel i en
 * lukking — er en delt tilstand i den mest sikkerhetskritiske stien i frontend.
 */
export function lagSesjonsspoerring(baseUrl: string, hent?: typeof fetch): Sesjonsspoerring {
  return async (token: string): Promise<Sesjonskrav> => {
    const klient = lagApiKlient(baseUrl, async () => token, hent);
    const res = await sessionGet({ client: klient });
    const status = res.response?.status ?? 0;
    if (!res.response?.ok || !res.data) throw ApiFeil.fra(status, res.error ?? res.data);
    const svar = res.data as AdminAdminSession;
    /**
     * Et felt som ikke er en `boolean` er ikke et svar. Kontrakten sier `boolean`, men en
     * generert type er en påstand om serveren og ikke en sjekk av den — og en `undefined`
     * som glir gjennom her ville blitt lest som «kravet gjelder ikke» ett steg senere.
     * Kastet lander som «kravet gjelder» i `AuthProvider`, som serveren gjør det.
     */
    if (typeof svar.adminMfaRequired !== 'boolean') throw new ApiFeil(status, 'unknown', 'Sesjonssvaret bar ikke tofaktorkravet.');
    return { adminMfaRequired: svar.adminMfaRequired, sessionExpiresAt: svar.sessionExpiresAt };
  };
}
